import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, Building2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, EmptyState } from "./_ui";

const EMPTY = { name: "", description: "", coordinator_name: "" };

export default function CoordinationsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/coordinations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Le nom est requis");
    setBusy(true);
    try {
      await api.post("/admin/coordinations", form);
      toast.success("Coordination créée");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette coordination ?")) return;
    await api.delete(`/admin/coordinations/${id}`);
    toast.success("Coordination supprimée"); await load();
  };

  if (!items) return (<><PageHeader title="Coordinations" /><Spinner /></>);

  return (
    <div data-testid="admin-coordinations">
      <PageHeader
        title="Coordinations"
        subtitle="Organes politiques et structures internes du mouvement A.F.B."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="coord-create-btn">
              <Plus size={16} /> Nouvelle coordination
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6" data-testid="coord-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom de la coordination" data-testid="coord-name" />
            <Input value={form.coordinator_name} onChange={(e) => setForm({ ...form, coordinator_name: e.target.value })} placeholder="Coordinateur (facultatif)" data-testid="coord-leader" />
          </div>
          <Textarea
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description (facultatif)" rows={2} className="mt-3"
            data-testid="coord-desc"
          />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={create} disabled={busy} data-testid="coord-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={Building2} title="Aucune coordination" hint="Créez la première coordination pour commencer." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card key={it.id} className="p-5" data-testid={`coord-${it.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0052CC]/15 grid place-items-center text-[#7AB0FF]">
                  <Building2 size={18} />
                </div>
                <button onClick={() => remove(it.id)} className="text-slate-500 hover:text-[#CE1126]" data-testid={`coord-delete-${it.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
              <h3 className="font-display text-lg text-white mt-3">{it.name}</h3>
              {it.coordinator_name && <div className="text-xs text-slate-400 mt-1">Coordinateur : {it.coordinator_name}</div>}
              {it.description && <p className="text-sm text-slate-400 mt-2 line-clamp-3">{it.description}</p>}
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-slate-500">Membres rattachés</span>
                <span className="font-display text-base text-[#D4AF37]">{it.members_count}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
